import { lazy } from 'react'
import { createBrowserRouter } from 'react-router'

import { AuthGuard } from '@shared/providers/src/MeProvider'

import AuthenticatedProviders from '@providers/AuthenticatedProviders'
import AuthBase from '@pages/Auth/components/AuthBase'
import Error from '@pages/Misc/Error'
import NotAuthorized from '@pages/Misc/NotAuthorized'
import NotFound from '@pages/Misc/NotFound'
import AppShell from '@components/Layout'

import Root from './Root'

const AuthRedirect = lazy(() => import('@pages/AuthRedirect'))
const Home = lazy(() => import('@pages/Home'))
const Environment = lazy(() => import('@pages/Environment'))
const FAQ = lazy(() => import('@pages/FAQ'))
const Enrollment = lazy(() => import('@pages/Auth/Enrollment'))
const AuthMagicLink = lazy(() => import('@pages/Auth/MagicLink'))
const AuthSignIn = lazy(() => import('@pages/Auth/SignIn'))
const AuthLinkSent = lazy(() => import('@pages/Auth/LinkSent'))
const AuthLinkRetry = lazy(() => import('@pages/Auth/LinkRetry'))
const ChannelVerified = lazy(() => import('@pages/Auth/ChannelVerified'))

const Account = lazy(() => import('@pages/Account'))
const AccountLayout = lazy(() => import('@pages/Account/AccountLayout'))
const Profile = lazy(() => import('@pages/Account/Profile'))
const Address = lazy(() => import('@pages/Account/Address'))
const Insurance = lazy(() => import('@pages/Account/Insurance'))
const HealthAssistance = lazy(() => import('@pages/Account/HealthAssistance'))
const HealthHistory = lazy(() => import('@pages/Account/HealthHistory'))
const AboutMe = lazy(() => import('@pages/Account/AboutMe'))
const Vitals = lazy(() => import('@pages/Account/Vitals/VitalsPage'))
const AccountPhotoId = lazy(() => import('@pages/Account/PhotoId'))
const Language = lazy(() => import('@pages/Account/Language'))
const Notifications = lazy(() => import('@pages/Account/Notifications'))
const ContactPreference = lazy(() => import('@pages/Account/ContactPreference'))
const PrimaryCareProvider = lazy(() => import('@pages/Account/PrimaryCareProvider'))
const Appointments = lazy(() => import('@pages/Appointments'))
const AppointmentDetailPage = lazy(() => import('@pages/Appointments/Appointment'))

const AppointmentAsyncMessaging = lazy(() => import('@pages/Appointments/Booking/AppointmentAsyncMessaging/AppointmentAsyncMessaging'))
const AppointmentBookingGuard = lazy(() => import('@pages/Appointments/Booking/AppointmentBookingGuard/AppointmentBookingGuard'))
const AppointmentOnDemand = lazy(() => import('@pages/Appointments/Booking/AppointmentOnDemand/AppointmentOnDemand'))
const AppointmentConfirm = lazy(() => import('@pages/Appointments/Booking/AppointmentSchedule/AppointmentConfirm'))
const AppointmentProviderDetails = lazy(() => import('@pages/Appointments/Booking/AppointmentSchedule/AppointmentProviderDetails'))
const AppointmentSchedule = lazy(() => import('@pages/Appointments/Booking/AppointmentSchedule/AppointmentSchedule'))
const AppointmentTreatment = lazy(() => import('@pages/Appointments/Booking/AppointmentTreatment'))
const AppointmentType = lazy(() => import('@pages/Appointments/Booking/AppointmentType'))

const AsyncVisit = lazy(() => import('@pages/AsyncVisit'))
const AsyncVisitLayout = lazy(() => import('@pages/AsyncVisit/AsyncVisitLayout'))
const AsyncVisitCompleted = lazy(() => import('@pages/AsyncVisit/AsyncVisitCompleted'))
const AsyncAssessment = lazy(() => import('@pages/AsyncAssessment'))
const AsyncAssessmentCompleted = lazy(() => import('@pages/AsyncAssessment/AsyncAssessmentCompleted'))
const AsyncAssessmentCanceled = lazy(() => import('@pages/AsyncAssessment/AsyncAssessmentCanceled'))

const AsyncVisitScheduleProviders = lazy(() => import('@pages/AsyncVisit/Booking/Schedule/ScheduleProviders'))
const AsyncVisitScheduleProvider = lazy(() => import('@pages/AsyncVisit/Booking/Schedule/ScheduleProvider'))
const AsyncVisitScheduleConfirm = lazy(() => import('@pages/AsyncVisit/Booking/Schedule/ScheduleConfirm'))
const AsyncVisitScheduleOnDemand = lazy(() => import('@pages/AsyncVisit/Booking/Schedule/ScheduleOnDemand'))
const AsyncVisitScheduleType = lazy(() => import('@pages/AsyncVisit/Booking/Schedule/ScheduleType'))

const Documents = lazy(() => import('@pages/Documents'))
const Health = lazy(() => import('@pages/Health'))
const Labs = lazy(() => import('@pages/Labs'))
const LabDetail = lazy(() => import('@pages/Labs/Lab'))
const HivLabs = lazy(() => import('@pages/HivLabs'))
const Payment = lazy(() => import('@pages/Payment'))
const Messages = lazy(() => import('@pages/Messages'))
const MessagesBlank = lazy(() => import('@pages/Messages/MessagesBlank'))
const SupportRedirect = lazy(() => import('@pages/Messages/SupportRedirect'))
const FullPageChat = lazy(() => import('@components/Chat/FullPageChat'))
const SupportRequestChat = lazy(() => import('@components/Chat/RequestChat/SupportRequestChat'))
const UserRequestChat = lazy(() => import('@components/Chat/RequestChat/UserRequestChat'))
const Prescriptions = lazy(() => import('@pages/Prescriptions'))
const PrescriptionDetail = lazy(() => import('@pages/Prescriptions/Prescription'))
const Visit = lazy(() => import('@pages/Visit'))
const OnDemand = lazy(() => import('@pages/OnDemand'))
const GileadApplication = lazy(() => import('@pages/GileadApplication'))
const RoiRequest = lazy(() => import('@pages/RoiRequest'))
const Redirect = lazy(() => import('@pages/Redirect'))
const Redirects = lazy(() => import('@pages/Redirects'))
const PhotoId = lazy(() => import('@pages/PhotoId'))
const Assessments = lazy(() => import('@pages/Assessments/Assessments'))
const Assessment = lazy(() => import('@pages/Assessments/Assessment/Assessment'))
const AssessmentDetail = lazy(() => import('@pages/Assessments/Assessment'))
const AssessmentTester = lazy(() => import('@pages/AssessmentTester'))

const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    children: [
      {
        path: '',
        element: <AuthRedirect />,
      },
      {
        path: '/auth',
        element: (
          <AppShell nav="none">
            <AuthBase />
          </AppShell>
        ),
        children: [
          {
            path: 'verify',
            element: <AuthMagicLink />,
          },
          {
            path: 'code',
            element: <AuthSignIn />,
          },
          {
            path: 'sent/:channel',
            element: <AuthLinkSent />,
          },
          {
            path: 'retry',
            element: <AuthLinkRetry />,
          },
          {
            path: 'enrollment',
            element: <Enrollment />,
          },
          {
            path: 'verified',
            element: <ChannelVerified />,
          },
        ],
      },
      /**
       * /app routes accessible only when user is authenticated.
       * AuthorizedProviders wrapper responsible for loading all necessary data before rendering the app
       */ {
        path: '/app',
        element: (
          <AuthGuard>
            <AuthenticatedProviders />
          </AuthGuard>
        ),
        children: [
          // These fill the window: no navigation, no content column
          { path: 'visits/:id', element: <Visit /> },
          { path: 'on-demand/:id', element: <OnDemand /> },
          { path: 'redirects/:value', element: <Redirects /> },
          // Everything else shares one shell, inside the providers its badge counts read from
          {
            element: <AppShell />,
            children: [
              {
                path: '',
                element: <Home />,
              },
              {
                path: 'account',
                element: <AccountLayout />,
                children: [
                  { path: '', element: <Account /> },
                  { path: 'profile', element: <Profile /> },
                  { path: 'address', element: <Address /> },
                  { path: 'insurance', element: <Insurance /> },
                  { path: 'health-assistance', element: <HealthAssistance /> },
                  { path: 'health-history', element: <HealthHistory /> },
                  { path: 'vitals', element: <Vitals /> },
                  { path: 'about', element: <AboutMe /> },
                  { path: 'photo-id', element: <AccountPhotoId /> },
                  { path: 'language', element: <Language /> },
                  { path: 'notifications', element: <Notifications /> },
                  { path: 'contact-preference', element: <ContactPreference /> },
                  { path: 'primary-care-provider', element: <PrimaryCareProvider /> },
                ],
              },
              {
                path: 'health',
                element: <Health />,
              },
              {
                path: 'faq',
                element: <FAQ />,
              },
              {
                path: 'photo-id',
                element: <PhotoId />,
              },
              {
                path: 'messages',
                children: [
                  { path: 'support', element: <SupportRedirect /> },
                  {
                    element: <Messages />,
                    children: [
                      { index: true, element: <MessagesBlank /> },
                      { path: 'support-request', element: <SupportRequestChat /> },
                      { path: 'new/:userId', element: <UserRequestChat /> },
                      { path: ':id', element: <FullPageChat /> },
                    ],
                  },
                ],
              },
              {
                path: 'async-messaging',
                children: [
                  {
                    path: ':id',
                    element: <AsyncVisitLayout />,
                    children: [
                      {
                        path: '',
                        element: <AsyncVisit />,
                      },
                      {
                        path: 'completed',
                        element: <AsyncVisitCompleted />,
                      },
                      {
                        path: 'booking',
                        children: [
                          // This is an appointment type selection for the Establish Appointment
                          {
                            path: 'type',
                            element: <AsyncVisitScheduleType />,
                          },
                          // This is a confirmation for the Establish On Demand Appointment type
                          {
                            path: 'on-demand',
                            element: <AsyncVisitScheduleOnDemand />,
                          },
                          // This is a Provider selection for the Establish Scheduling Appointment type
                          {
                            path: 'providers',
                            element: <AsyncVisitScheduleProviders />,
                          },
                          // This is a date and time selection for the Establish Scheduling Appointment type
                          {
                            path: 'provider',
                            element: <AsyncVisitScheduleProvider />,
                          },
                          // This is a confirmation for the Establish Scheduling Appointment type
                          {
                            path: 'schedule',
                            element: <AsyncVisitScheduleConfirm />,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: 'assessments',
                children: [
                  {
                    path: '',
                    element: <Assessments />,
                  },
                  {
                    path: ':id',
                    element: <AssessmentDetail />,
                  },
                  {
                    path: 'flow/:id',
                    element: <Assessment />,
                  },
                  {
                    path: 'async/:id',
                    element: <AsyncAssessment />,
                  },
                  {
                    path: 'async/:id/completed',
                    element: <AsyncAssessmentCompleted />,
                  },
                  {
                    path: 'async/:id/canceled',
                    element: <AsyncAssessmentCanceled />,
                  },
                ],
              },
              ...(process.env.VITE_BUILD_ENV !== 'production'
                ? [
                    {
                      path: 'assessments/tester',
                      element: <AssessmentTester />,
                    },
                  ]
                : []),
              {
                path: 'labs',
                children: [
                  { path: '', element: <Labs /> },
                  { path: ':id', element: <LabDetail /> },
                ],
              },
              {
                path: 'prescriptions',
                children: [
                  { path: '', element: <Prescriptions /> },
                  { path: ':id', element: <PrescriptionDetail /> },
                ],
              },
              {
                path: 'appointments',
                children: [
                  {
                    path: '',
                    element: <Appointments />,
                  },
                  {
                    path: ':id',
                    element: <AppointmentDetailPage />,
                  },
                  {
                    path: 'booking',
                    element: <AppointmentBookingGuard />,
                    children: [
                      // This is an appointment treatment selection
                      {
                        path: ':appointmentId?/treatment',
                        element: <AppointmentTreatment />,
                      },
                      // This is an appointment type selection OR direct appointment type if only 1 type is available
                      {
                        path: ':appointmentId?/type',
                        element: <AppointmentType />,
                      },
                      // This is confirmation for the Async Messaging appointment type
                      {
                        path: ':appointmentId?/async-messaging',
                        element: <AppointmentAsyncMessaging />,
                      },
                      // This is confirmation for the On Demand appointment type
                      {
                        path: ':appointmentId?/on-demand',
                        element: <AppointmentOnDemand />,
                      },
                      // This is a Provider selection for the Scheduling Appointment type
                      {
                        path: ':appointmentId?/schedule',
                        element: <AppointmentSchedule />,
                      },
                      // This is a date and time selection for the Scheduling Appointment type
                      {
                        path: ':appointmentId?/provider-details',
                        element: <AppointmentProviderDetails />,
                      },
                      // This is a confirmation for the Scheduling Appointment type
                      {
                        path: ':appointmentId?/confirm',
                        element: <AppointmentConfirm />,
                      },
                    ],
                  },
                ],
              },
              {
                path: 'documents',
                element: <Documents />,
              },
              {
                path: 'gilead-application/:id',
                element: <GileadApplication />,
              },
              {
                path: 'roi-request/:id',
                element: <RoiRequest />,
              },
              {
                path: 'hiv-labs/:id',
                element: <HivLabs />,
              },
              {
                path: 'payments/:id',
                element: <Payment />,
              },
            ],
          },
        ],
      },
      {
        path: '/environment',
        element: <Environment />,
      },
      /**
       * Acts as a deep link entrypoint.
       * When a user follows a deep link and requires authentication,
       * once auth is completed (admin app) they are sent back here.
       * The url will include a `to` query param with the deep link.
       */ {
        path: '/redirect',
        element: <Redirect />,
      },
      {
        path: '*',
        children: [
          {
            path: '401',
            element: <NotAuthorized />,
          },
          {
            path: '404',
            element: <NotFound />,
          },
          {
            path: '500',
            element: <Error />,
          },
          {
            path: '*',
            element: <NotFound />,
          },
        ],
      },
    ],
  },
])

export default router
